import type { HTMLMotionProps } from 'motion/react'
import { cloneElement, isValidElement, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { motion } from 'motion/react'
import clsx from 'clsx';
import type { IconProps } from '../icons/types';
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint';
import { playPopupCloseSound } from '@/src/game/audio/uiSounds';

type ButtonIconProps = Omit<HTMLMotionProps<'button'>, 'color'> & {
    icon: ReactNode;
    variant?: 'tab' | 'standalone' | 'move' | 'control' | 'standaloneSmall'
    color?: 'blue' | 'orange' | 'red' | 'green'
    popupCloseSound?: boolean
    activateOn?: 'pointerdown' | 'pointerup'
    className?: string;
}

const iconPropsByColor: Record<NonNullable<ButtonIconProps['color']>, IconProps> = {
    blue: {
        stroke: '#0A2897',
        stopColor: '#C8E3F7',
    },
    red: {
        stroke: '#7F0016',
        stopColor: '#F3A9B6',
    },
    orange: {
        stroke: '#8A0B00',
        stopColor: '#FFD080',
    },
    green: {
        stroke: '#1F4B0E',
        stopColor: '#D9D9D9',
    },
}

const disabledIconProps: IconProps = {
    stroke: '#7B7E91',
    stopColor: '#C8E3F7',
}

export default function ButtonIcon({
    icon,
    color = 'blue',
    variant = 'tab',
    popupCloseSound = false,
    activateOn = 'pointerdown',
    disabled,
    className,
    ...buttonProps
}: ButtonIconProps) {
    const {
        onClick,
        onPointerDown,
        onPointerUp,
        onPointerCancel,
        ...restButtonProps
    } = buttonProps
    const iconProps = {
        ...iconPropsByColor[color],
        ...(disabled ? disabledIconProps : {}),
    }
    const renderedIcon = isValidElement<IconProps>(icon)
        ? cloneElement(icon, iconProps)
        : icon

    const isStele = useSteleBreakpoint();

    const animateTabY = isStele ? -10 : -2.5
    const animateStandaloneY = isStele ? -14 : -3.5
    const animateMoveY = isStele ? -16 : -4
    const animateControlY = isStele ? -16 : -4

    const triggerPress = (
        event: ReactPointerEvent<HTMLButtonElement> | ReactMouseEvent<HTMLButtonElement>,
    ) => {
        if (disabled) return

        if (popupCloseSound) {
            playPopupCloseSound()
        }

        onClick?.(event as ReactMouseEvent<HTMLButtonElement>)
    }

    const handlePointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
        onPointerDown?.(event)

        if (disabled) return

        if (event.pointerType === 'touch') {
            event.preventDefault()
        }

        if (activateOn === 'pointerdown') {
            triggerPress(event)
        }
    }

    const handlePointerUp = (event: ReactPointerEvent<HTMLButtonElement>) => {
        onPointerUp?.(event)

        if (activateOn === 'pointerup') {
            triggerPress(event)
        }
    }

    return (
        <div className={clsx(
            'relative flex justify-center items-center rounded-full',
            variant === 'tab' && 'bg-white-stroke bg-none w-9.25 h-9.25 min-w-9.25 min-h-9.25 max-w-9.25 max-h-9.25 p-px | stele:w-37 stele:h-37 stele:min-w-37 stele:min-h-37 stele:max-w-37 stele:max-h-37 stele:p-1',
            variant === 'standalone' && 'bg-white-gradient w-13 h-13 min-w-13 min-h-13 max-w-13 max-h-13 drop-shadow-tab-controls p-1 | stele:w-48 stele:h-48 stele:min-w-48 stele:min-h-48 stele:max-w-48 stele:max-h-48 stele:drop-shadow-tab-controls stele:p-4',
            variant === 'standaloneSmall' && 'bg-white-gradient w-11 h-11 min-w-11 min-h-11 max-w-11 max-h-11 drop-shadow-tab-controls p-1 | stele:w-42 stele:h-42 stele:min-w-42 stele:min-h-42 stele:max-w-42 stele:max-h-42 stele:drop-shadow-tab-controls stele:p-3',
            variant === 'move' && 'bg-white-gradient w-17 h-17 min-w-17 min-h-17 max-w-17 max-h-17 drop-shadow-tab-controls p-1 stele:w-60 stele:h-60 stele:min-w-60 stele:min-h-60 stele:max-w-60 stele:max-h-60 stele:p-1 stele:drop-shadow-none',
            variant === 'control' && 'bg-none w-11 h-11 min-w-11 min-h-11 max-w-11 max-h-11 p-px | stele:w-44 stele:h-44 stele:min-w-44 stele:min-h-44 stele:max-w-44 stele:max-h-44 stele:p-1',
            className
        )}>
            <div className={clsx(
                'flex justify-center items-center rounded-full w-full h-full',
                color === 'blue' && 'bg-blue-stroke',
                color === 'red' && 'bg-red-stroke',
                color === 'orange' && 'bg-orange-stroke',
                color === 'green' && 'bg-green-stroke',
                disabled && 'border-transparent! bg-transparent!',
            )}>
                <motion.button
                    {...restButtonProps}
                    type={'button'}
                    disabled={disabled}
                    onPointerDown={handlePointerDown}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={onPointerCancel}
                    initial={false}
                    animate={
                        disabled
                            ? { y: 0 }
                            : variant === 'tab'
                                ? { y: animateTabY }
                                : variant === 'standalone'
                                    ? { y: animateStandaloneY }
                                    : variant === 'move'
                                        ? { y: animateMoveY }
                                        : { y: animateControlY }
                    }
                    whileTap={
                        disabled
                            ? undefined
                            : { y: 0 }
                    }
                    transition={{
                        y: {
                            type: 'spring',
                            stiffness: 520,
                            damping: 30,
                            mass: 0.7,
                        },
                    }}
                    className={clsx(
                        'relative flex justify-center items-center bg-blue-gradient rounded-full w-full h-full',
                        'border-2',
                        'stele:border-8',
                        variant === 'move' && 'after:absolute after:-inset-6 after:block after:content-[\'\'] stele:after:inset-y-0 stele:after:-left-50 stele:after:-right-50 stele:after:-bottom-6 stele:after:-top-6',
                        color === 'blue' && 'bg-blue-gradient border-blue-stroke',
                        color === 'red' && 'border-red-stroke bg-red-gradient',
                        color === 'orange' && 'border-orange-stroke bg-orange-gradient',
                        color === 'green' && 'border-green-stroke bg-green-gradient',
                        disabled && 'border-white-stroke! bg-grey-gradient!',

                    )}
                >
                    <div
                        className={clsx(
                            'absolute bg-shine-gradient rounded-full',
                            variant === 'tab' && 'top-0.75 w-4 h-4 | stele:top-3 stele:w-16 stele:h-16',
                            variant === 'standalone' && 'top-0.75 w-4 h-4 | stele:top-3 stele:w-16 stele:h-16',
                            variant === 'standaloneSmall' && 'top-0.5 w-4 h-4 | stele:top-3 stele:w-16 stele:h-16',
                            variant === 'move' && 'top-1 w-8 h-8 | stele:top-4 stele:w-32 stele:h-32',
                            variant == 'control' && 'top-0.5 w-6 h-6 | stele:top-2 stele:w-24 stele:h-24',
                        )}></div>
                    <div className={clsx(
                        'flex justify-center items-center',
                        variant === 'tab' && 'max-w-6 max-h-6 | stele:max-w-24 stele:max-h-24',
                        variant === 'standalone' && 'max-w-7 max-h-7 | stele:max-w-28 stele:max-h-28',
                        variant === 'standalone' && 'max-w-7 max-h-7 | stele:max-w-28 stele:max-h-28',
                        variant === 'move' && ' max-w-9.5 max-h-9.5 | stele:max-w-38 stele:max-h-38',
                        variant === 'control' && 'max-w-7 max-h-7 | stele:max-w-28 stele:max-h-28'
                    )}>
                        {renderedIcon}
                    </div>
                </motion.button>
            </div>
        </div>
    )
}
